import {
  AddOnRateType,
  BillingLine_Read,
  BillingLineAddOnChargeKind,
  BillingLineContext,
  BillingLineGroup_Read,
  BillingLineKind,
  BillingLineSide,
  BillingState,
  FuelSurchargeScheduleType,
  LineIneligibilityReason,
  RejectedReason,
} from '@treadinc/horizon-api-spec';

import { AddOnCharge } from '~hooks/useAddOns/models';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { toDecimal } from '~utils/decimal';

class BillingLineScope {
  constructor(
    private _party: BillingLineContext['party'],
    private _job: BillingLineContext['job'],
    private _load: BillingLineContext['load'],
  ) {}

  public get party() {
    return this._party;
  }

  public get job() {
    return this._job;
  }

  public get load() {
    return this._load;
  }

  public static parse(proto: BillingLineContext) {
    return new BillingLineScope(proto.party, proto.job, proto.load);
  }
}

export class BillingLine {
  constructor(
    private _kind: BillingLineKind,
    private _side: BillingLineSide,
    private _scope: BillingLineScope,
    private _eligible: boolean,
    private _ineligibility_reasons: LineIneligibilityReason[],
    private _invoice_line_item: InvoiceLineItem | null = null,
    private _add_on_charge: AddOnCharge | null = null,
  ) {}

  public get kind() {
    return this._kind;
  }

  public get side() {
    return this._side;
  }

  public get scope() {
    return this._scope;
  }

  public get eligible() {
    return this._eligible;
  }

  public get ineligibilityReasons() {
    return this._ineligibility_reasons;
  }

  public get invoiceLineItem() {
    return this._invoice_line_item;
  }

  public get addOnCharge() {
    return this._add_on_charge;
  }

  public get id() {
    return (this._invoice_line_item ?? this._add_on_charge)?.id ?? null;
  }

  public get billingState() {
    return (this._invoice_line_item ?? this._add_on_charge)?.billingState ?? null;
  }

  public get name() {
    return (this._invoice_line_item ?? this._add_on_charge)?.name ?? null;
  }

  public get quantity() {
    return (this._invoice_line_item ?? this._add_on_charge)?.quantity ?? null;
  }

  public get rateType() {
    return (
      this._invoice_line_item?.rateType ??
      this._add_on_charge?.revenueCostRateType ??
      null
    );
  }

  public get isPercentRate() {
    const charge = this._add_on_charge;
    const isPercentOfTotal =
      charge?.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL;
    const isPercentageFuelSurcharge =
      charge?.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
      charge.surchargeType === FuelSurchargeScheduleType.PERCENTAGE;

    return isPercentOfTotal || isPercentageFuelSurcharge;
  }

  // A percent-of-total add-on keeps its percent in `percentage`; its rate value reads 0.
  public get rate() {
    const charge = this._add_on_charge;

    if (this.isPercentRate && charge?.percentage) {
      return charge.percentage;
    }

    return (this._invoice_line_item ?? charge)?.rate ?? null;
  }

  public get rejectedReason() {
    return (this._invoice_line_item ?? this._add_on_charge)?.rejectedReason ?? null;
  }

  public get totalAmount() {
    return (this._invoice_line_item ?? this._add_on_charge)?.totalAmount ?? 0;
  }

  public get settlement() {
    return (this._invoice_line_item ?? this._add_on_charge)?.settlement ?? null;
  }

  // Only an explicit false stops the line being changed; null means the API did not say.
  public get editable() {
    return (this._invoice_line_item ?? this._add_on_charge)?.editable ?? null;
  }

  public get isRejected() {
    return (
      this.billingState === BillingState.REJECTED ||
      this.billingState === BillingState.NOT_APPLIED
    );
  }

  public get allowedRejectionReasons() {
    if (this.billingState !== BillingState.UNSETTLED || this.editable === false) {
      return [];
    }

    const isFlatRateAddOn =
      this._add_on_charge?.revenueCostRateType === AddOnRateType.RATE_FOR_EACH;

    return isFlatRateAddOn
      ? [RejectedReason.NEVER_BILL, RejectedReason.NOT_APPLIED]
      : [RejectedReason.NEVER_BILL];
  }

  public get canUnreject() {
    return this.isRejected && this.editable !== false;
  }

  public get wasRate() {
    return this._invoice_line_item?.hasRateVariance
      ? this._invoice_line_item.derivedRate
      : null;
  }

  public get wasTotalAmount() {
    return this._add_on_charge?.hasTotalVariance
      ? this._add_on_charge.derivedTotalAmount
      : null;
  }

  public static parse(proto: BillingLine_Read) {
    const scope = BillingLineScope.parse(proto.context);

    if (proto.kind === BillingLineAddOnChargeKind.ADD_ON_CHARGE) {
      return new BillingLine(
        BillingLineKind.ADD_ON_CHARGE,
        proto.side,
        scope,
        proto.eligible,
        proto.ineligibility_reasons,
        null,
        AddOnCharge.parse(proto),
      );
    }

    return new BillingLine(
      BillingLineKind.INVOICE_LINE_ITEM,
      proto.side,
      scope,
      proto.eligible,
      proto.ineligibility_reasons,
      InvoiceLineItem.parse(proto),
      null,
    );
  }
}

export class BillingLineGroup {
  constructor(
    private _side: BillingLineSide,
    private _line_count: number,
    private _total_amount: number,
    private _eligible_count: number,
  ) {}

  public get side() {
    return this._side;
  }

  public get lineCount() {
    return this._line_count;
  }

  public get totalAmount() {
    return this._total_amount;
  }

  public get eligibleCount() {
    return this._eligible_count;
  }

  public static parse(proto: BillingLineGroup_Read) {
    return new BillingLineGroup(
      proto.side,
      proto.line_count,
      toDecimal(proto.total_amount).toNumber(),
      proto.eligible_count,
    );
  }
}
